import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { ConfigContext } from 'Context/config';

import { assignSectionRoutes } from 'Utilities/routing';
import { fetchBlogData, fetchPageData, getAuthorDetailsData, getGuidePage } from 'Utilities/wordpressPages';
import { getGuideAuthorsData } from 'Utilities/wordpressPages/wordpressGuideAuthorsData';

import { WordpressData, WordpressRouteParams } from '../@types/Wordpress';
import { TIMING } from '../utilities/WordpressConstants';

import { useScroll } from './useScroll';
import { ProcessedBlogData, ProcessedGuideAuthorsData, ProcessedPageData, useWordpressDataFormatHelpers } from './useWordpressDataFormatHelpers';

let ssrDataConsumed = false;

export const resetSsrDataConsumedForTests = () => {
  ssrDataConsumed = false;
};

export type WordpressState = {
  data: WordpressData | null;
  empty: boolean;
  error: boolean;
  isAuthorDetails: boolean;
  isBlog: boolean;
  isGuideAuthors: boolean;
  isPage: boolean;
  isSSR: boolean;
  ready: boolean;
};

type WordpressAction = {
  payload?: {
    data: WordpressData;
    empty?: boolean;
    isAuthor?: boolean;
    isAuthorDetails?: boolean;
    isBlog?: boolean;
    isGuideAuthors?: boolean;
    isPage?: boolean;
  };
  type: 'ERROR' | 'SUCCESS' | 'LOADING';
};

export const useWPData = () => {
  const location = useLocation();
  const params = useParams<WordpressRouteParams>();
  const match = useRouteMatch();

  const context = useContext(ConfigContext);
  const ssrData = ssrDataConsumed ? null : (context)?.configState?.wordpressData || null;
  const { processBlogData, processGuideAuthorsData, processPageData } = useWordpressDataFormatHelpers();
  const { scrollToHash } = useScroll();

  const {
    isBlogRoute,
    isGuideAuthorDetailsRoute,
    isGuideAuthorsRoute,
    isGuideRoute,
  } = assignSectionRoutes({ params, path: match.path });
  const previousPathRef = useRef(location.pathname);
  const isInitialMount = useRef(true);

  // Check if SSR data is valid, non-empty, and matches the current route type
  const ssrMatchesRoute = ssrData && (
    (ssrData.posts && ssrData.posts.length > 0)
    || (ssrData?.guideAuthors && ssrData?.guideAuthors.length > 0)
    || (ssrData.content && ssrData.content.length > 0)
  );

  // Format SSR data for initial state if available
  let formattedSSRData: ProcessedBlogData | ProcessedPageData | ProcessedGuideAuthorsData | null = null;
  if (ssrMatchesRoute) {
    let formatData;

    switch (true) {
    case isGuideAuthorsRoute:
    case isGuideAuthorDetailsRoute:
      formatData = processGuideAuthorsData;
      break;
    case isBlogRoute:
      formatData = processBlogData;
      break;
    default:
      formatData = processPageData;
      break;
    }
    formattedSSRData = formatData?.(ssrData);
  }

  const initialState: WordpressState = {
    data: formattedSSRData?.data || null,
    empty: (formattedSSRData as ProcessedBlogData)?.empty || false,
    error: false,
    isAuthorDetails: !!isGuideAuthorDetailsRoute,
    isBlog: isBlogRoute,
    isGuideAuthors: !!isGuideAuthorsRoute,
    isPage: !isBlogRoute && !isGuideAuthorsRoute && !isGuideAuthorDetailsRoute,
    isSSR: !!ssrMatchesRoute,
    ready: !!formattedSSRData,
  };

  const [state, dispatch] = useReducer((currentState: WordpressState, action: WordpressAction) => {
    switch (action.type) {
    case 'LOADING':
      return {
        ...currentState,
        data: null,
        empty: false,
        error: false,
        isAuthorDetails: false,
        isBlog: false,
        isGuideAuthors: false,
        isPage: false,
        isSSR: false,
        ready: false,
      };
    case 'SUCCESS':
      return { ...currentState, ...action.payload, error: false, ready: true };
    case 'ERROR':
      return { ...currentState, data: null, empty: true, error: true, ready: true };
    default:
      return currentState;
    }
  }, initialState);

  const getFetchStrategies = useCallback(() => ({
    authorDetails: {
      condition: isGuideAuthorDetailsRoute,
      fetch: () => getAuthorDetailsData(params?.author),
      format: processGuideAuthorsData,
      type: 'authorDetails',
    },
    blog: {
      condition: isBlogRoute,
      fetch: () => fetchBlogData(location.pathname),
      format: processBlogData,
      type: 'blog',
    },
    guideAuthors: {
      condition: isGuideAuthorsRoute,
      fetch: () => getGuideAuthorsData(),
      format: processGuideAuthorsData,
      type: 'guideAuthors',
    },
    page: {
      condition: true,
      fetch: () => {
        const urlSlug = params?.subdir || location.pathname.substring(1);
        return isGuideRoute
          ? getGuidePage({ route: params?.subdir, slug: location.pathname })
          : fetchPageData(urlSlug, isGuideRoute);
      },
      format: processPageData,
      type: isGuideRoute ? 'guide' : 'page',
    },
  }), [
    isGuideAuthorDetailsRoute, isBlogRoute, isGuideAuthorsRoute, isGuideRoute,
    location.pathname, params,
    processBlogData, processGuideAuthorsData, processPageData,
  ]);

  const fetchContentData = useCallback(() => {
    const fetchStrategies = getFetchStrategies();
    const [strategyKey, strategy] = Object.entries(fetchStrategies).find(
      ([, fetchStrategy]) => fetchStrategy.condition,
    ) || ['page', fetchStrategies.page];

    const { fetch: fetchContent, format, type } = strategy;

    fetchContent()
      .then((data) => {
        if (data) {
          const formatted = format(data);
          if (formatted) {
            dispatch({
              payload: {
                ...formatted,
                isAuthorDetails: isGuideAuthorDetailsRoute,
                isBlog: isBlogRoute,
                isGuideAuthors: isGuideAuthorsRoute,
                isPage: !isBlogRoute && !isGuideAuthorsRoute && !isGuideAuthorDetailsRoute,
              },
              type: 'SUCCESS',
            });
          } else {
            throw new Error(`${strategyKey} data formatting failed`);
          }
        } else {
          throw new Error(`No ${strategyKey} data received`);
        }
      })
      .catch((error) => {
        globalThis?.Sentry?.captureException(error as Error, {
          extra: {
            path: location.pathname,
            type,
          },
        });
        dispatch({ type: 'ERROR' });
      });
  }, [
    getFetchStrategies,
    isGuideAuthorDetailsRoute,
    isBlogRoute,
    isGuideAuthorsRoute,
    location.pathname,
  ]);

  useEffect(() => {
    const isPathChanged = previousPathRef.current !== location.pathname;

    if ((isPathChanged || isInitialMount.current) && (isPathChanged || !formattedSSRData)) {
      dispatch({ type: 'LOADING' });
      fetchContentData();
      previousPathRef.current = location.pathname;
    }

    isInitialMount.current = false;
    ssrDataConsumed = true;
  }, [location.pathname, fetchContentData, formattedSSRData]);

  useEffect(() => {
    if (location.hash && state.ready) {
      setTimeout(() => scrollToHash(), TIMING.SCROLL_DELAY);
    }
  }, [location.hash, state.ready, scrollToHash]);

  return { ...state, isGuide: isGuideRoute };
};
